
import React from 'react';

export interface Invoice {
  id: string;
  title: string;
  customer: string;
  customerEmail?: string;
  customerBillingAddress?: string;
  customerCountry?: string;
  customerState?: string;
  paymentType?: 'bank' | 'card';
  date: string;
  amount: number;
  status: 'paid' | 'pending' | 'expired';
  description?: string;
  lineItems?: LineItem[];
  isSubscription?: boolean;
  invoiceType?: 'one_time' | 'recurring';
  includeOneTime?: boolean;
  recurringPeriod?: string;
  heroImage?: string;
  isSample?: boolean;
}

export interface LineItem {
  name: string;
  description?: string;
  amount: number;
  isSubscription?: boolean;
  recurringPeriod?: string;
}

export const sampleInvoice: Invoice = {
  id: 'SAMPLE001',
  title: 'Website Design',
  customer: 'Acme Corp',
  customerEmail: 'john.doe@example.com',
  customerBillingAddress: '123 Main St, Anytown, USA',
  customerCountry: 'us',
  customerState: 'ca',
  date: '2024-03-15',
  amount: 1200,
  status: 'pending',
  description: 'Design and development of a new website',
  lineItems: [
    { name: 'Design', description: 'Website design', amount: 500 },
    { name: 'Development', description: 'Front-end and back-end development', amount: 700 }
  ],
  isSubscription: false,
  recurringPeriod: null,
  heroImage: 'business',
  isSample: true
};

export const globalInvoices: Invoice[] = [
  {
    id: 'INV001',
    title: 'Software Subscription',
    customer: 'Beta Co',
    customerEmail: 'jane.smith@example.com',
    customerBillingAddress: '456 Elm St, Anytown, USA',
    customerCountry: 'us',
    customerState: 'ny',
    paymentType: 'bank',
    date: '2024-03-01',
    amount: 300,
    status: 'paid',
    description: 'Monthly subscription for software services',
    lineItems: [
      { name: 'Subscription', description: 'Monthly software access', amount: 300, isSubscription: true }
    ],
    isSubscription: true,
    recurringPeriod: 'Monthly',
    heroImage: 'tech',
    isSample: false
  },
  {
    id: 'INV002',
    title: 'Consulting Services',
    customer: 'Gamma Inc',
    customerEmail: 'robert.jones@example.com',
    customerBillingAddress: '789 Oak St, Anytown, USA',
    customerCountry: 'us',
    customerState: 'tx',
    paymentType: 'card',
    date: '2024-02-15',
    amount: 750,
    status: 'expired',
    description: 'Consulting services for business strategy',
    lineItems: [
      { name: 'Consulting', description: 'Strategic consulting services', amount: 750 }
    ],
    isSubscription: false,
    recurringPeriod: null,
    heroImage: 'abstract',
    isSample: false
  },
  {
    id: 'INV003',
    title: 'Web Hosting',
    customer: 'Delta Ltd',
    customerEmail: 'emily.williams@example.com',
    customerBillingAddress: '101 Pine St, Anytown, USA',
    customerCountry: 'uk',
    paymentType: 'bank',
    date: '2024-03-10',
    amount: 150,
    status: 'pending',
    description: 'Monthly web hosting services',
    lineItems: [
      { name: 'Hosting', description: 'Monthly web hosting', amount: 150, isSubscription: true }
    ],
    isSubscription: true,
    recurringPeriod: 'Monthly',
    heroImage: 'minimal',
    isSample: false
  },
  {
    id: 'INV004',
    title: 'Marketing Campaign',
    customer: 'Epsilon Group',
    customerEmail: 'david.brown@example.com',
    customerBillingAddress: '222 Cedar St, Anytown, USA',
    customerCountry: 'ca',
    paymentType: 'bank',
    date: '2024-02-28',
    amount: 900,
    status: 'paid',
    description: 'Marketing campaign for product launch',
    lineItems: [
      { name: 'Marketing', description: 'Campaign management and execution', amount: 900 }
    ],
    isSubscription: false,
    recurringPeriod: null,
    heroImage: 'business',
    isSample: false
  },
  {
    id: 'INV005',
    title: 'SEO Services',
    customer: 'Zeta Co',
    customerEmail: 'linda.davis@example.com',
    customerBillingAddress: '333 Birch St, Anytown, USA',
    customerCountry: 'au',
    paymentType: 'card',
    date: '2024-03-05',
    amount: 400,
    status: 'pending',
    description: 'Monthly SEO services',
    lineItems: [
      { name: 'SEO', description: 'Search engine optimization services', amount: 400, isSubscription: true }
    ],
    isSubscription: true,
    recurringPeriod: 'Monthly',
    heroImage: 'tech',
    isSample: false
  }
];

// Function to add a new invoice to the global invoices list
export const addInvoice = (invoice: Invoice) => {
  globalInvoices.push({...invoice, isSample: false});
};

// Create a default export to resolve the error in Dashboard.tsx
const InvoiceList = () => {
  return (
    <div className="space-y-4">
      <table className="w-full">
        <thead>
          <tr className="text-sm text-gray-600 border-b">
            <th className="pb-2 text-left font-medium">Invoice</th>
            <th className="pb-2 text-left font-medium">Customer</th>
            <th className="pb-2 text-left font-medium">Date</th>
            <th className="pb-2 text-left font-medium">Amount</th>
            <th className="pb-2 text-left font-medium">Status</th>
            <th className="pb-2 text-right font-medium">Actions</th>
          </tr>
        </thead>
        <tbody>
          {globalInvoices.map((invoice) => (
            <tr key={invoice.id} className="border-b text-sm hover:bg-gray-50">
              <td className="py-3">
                <a href={`/invoice/${invoice.id}`} className="text-blue-600 hover:underline">
                  {invoice.title}
                </a>
              </td>
              <td className="py-3">{invoice.customer}</td>
              <td className="py-3">{new Date(invoice.date).toLocaleDateString()}</td>
              <td className="py-3">${invoice.amount.toLocaleString()}</td>
              <td className="py-3">
                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium
                  ${invoice.status === 'paid' ? 'bg-green-100 text-green-800' : ''}
                  ${invoice.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : ''}
                  ${invoice.status === 'expired' ? 'bg-gray-100 text-gray-800' : ''}
                `}>
                  {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                </span>
              </td>
              <td className="py-3 text-right">
                <a href={`/invoice/${invoice.id}`} className="text-sm text-gray-600 hover:text-gray-900">
                  View
                </a>
              </td>
            </tr>
          ))}
          {sampleInvoice && (
            <tr className="border-b text-sm hover:bg-gray-50">
              <td className="py-3">
                <a href={`/invoice/SAMPLE001`} className="text-blue-600 hover:underline">
                  {sampleInvoice.title} (Sample)
                </a>
              </td>
              <td className="py-3">{sampleInvoice.customer}</td>
              <td className="py-3">{new Date(sampleInvoice.date).toLocaleDateString()}</td>
              <td className="py-3">${sampleInvoice.amount.toLocaleString()}</td>
              <td className="py-3">
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                  Sample
                </span>
              </td>
              <td className="py-3 text-right">
                <a href={`/invoice/SAMPLE001`} className="text-sm text-gray-600 hover:text-gray-900">
                  View
                </a>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default InvoiceList;
